import { useEffect, useState, type ReactNode } from "react";
import type { RcFile, UploadChangeParam } from "antd/es/upload";

interface IDocumentSelector {
  onFileSelect: (file: RcFile) => void;
  onFileRemove: () => void;
  defaultFileName?: string;
  defaultFileLink?: string;
  defaultFileSize?: number;
  children: ({
    selected,
    handleFileSelect,
    handleFileRemove,
    selectedFileName,
    selectedFileLink,
    selectedFileSize,
  }: {
    selected: boolean;
    handleFileSelect: (info: UploadChangeParam) => void;
    handleFileRemove: () => void;
    selectedFileName?: string | null;
    selectedFileLink?: string | null;
    selectedFileSize?: number | null;
  }) => ReactNode;
}

export const DocumentSelectorWrapper = ({
  onFileSelect,
  onFileRemove,
  defaultFileName,
  defaultFileLink,
  defaultFileSize,
  children,
}: IDocumentSelector) => {
  const [selectedFileName, setSelectedFileName] = useState<
    string | null | undefined
  >(defaultFileName ?? null);

  const [selectedFileLink, setSelectedFileLink] = useState<
    string | null | undefined
  >(defaultFileLink ?? null);

  const [selectedFileSize, setSelectedFileSize] = useState<
    number | null | undefined
  >(defaultFileSize ?? null);

  useEffect(() => {
    setSelectedFileName(defaultFileName);
  }, [defaultFileName]);

  useEffect(() => {
    setSelectedFileLink(defaultFileLink);
  }, [defaultFileLink]);

  useEffect(() => {
    setSelectedFileSize(defaultFileSize);
  }, [defaultFileSize]);

  const handleFileRemove = () => {
    if (selectedFileLink?.startsWith("blob")) {
      URL.revokeObjectURL(selectedFileLink);
    }
    setSelectedFileName(null);
    onFileRemove();
  };

  const handleFileSelect = (info: UploadChangeParam) => {
    const file = info.fileList?.at(0);
    if (file?.originFileObj) {
      const link = URL.createObjectURL(file.originFileObj);
      setSelectedFileName(file.originFileObj.name);
      setSelectedFileLink(link);
      setSelectedFileSize(file.originFileObj.size);
      if (onFileSelect) {
        onFileSelect(file.originFileObj);
      }
    }
  };

  return children({
    selected: Boolean(selectedFileName),
    handleFileSelect,
    handleFileRemove,
    selectedFileLink,
    selectedFileSize,
    selectedFileName,
  });
};
